import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';

import { catchError, map, mergeMap, switchMap } from 'rxjs/operators';

import * as maintenanceActions from './maintenance.actions';
import {
    AddRecurrenceAction,
    AddTaskAction,
    DeleteActivityAction,
    DeleteMaintenancePlanAction,
    DeleteRecurrenceAction,
    DeleteTaskAction,
    LoadPlanAction,
    LoadPlansAction,
    LoadPlanToolsAction,
    LoadRecurrencesAction,
    UpdateActivityAction,
    UpdatePlanAction,
    UpdateRecurrenceAction,
    UpdateTaskAction,
    UpdateTaskPhotosAction
} from './maintenance.actions';

import { RecurrenceService } from '@app/maintenance/service/recurrence.service';
import { of } from 'rxjs';
import { Recurrence } from '@app/shared/models/recurrence.model';
import { ActivitiesService } from '../service/activities.service';
import { Activity } from '@app/shared/models/activity.model';
import { TasksService } from '../service/tasks.service';
import { ActivityTask } from '@models/activity-task.model';
import { MaintenancePlansService } from '../service/maintenance-plans.service';
import { MaintenancePlan } from '@app/shared/models/maintenancePlan.model';
import { Tool } from '@app/tool/model/tool.model';
import { ToolSet } from '@app/tool/model/toolSet.model';
import { RoleService } from "@services/role.service";
import { TaskInstructionType } from "@models/enums/task-instruction-type.enum";

@Injectable()
export class MaintenanceEffects {

    // tslint:disable-next-line
    addRecurrence$ = createEffect(() => this.actions$.pipe(
        ofType(maintenanceActions.ADD_RECURRENCE),
        switchMap((addAction: AddRecurrenceAction) => {
            return this.recurrenceService.create(addAction.payload).pipe(
                map((recurrence: any) => {
                    return new maintenanceActions.AddRecurrenceSuccessAction(this.mapRecurrence(recurrence))
                }),
                catchError((error) => of(new maintenanceActions.AddRecurrenceFailedAction(error)))
            )
        })
    ));

    loadRecurrences$ = createEffect(() => this.actions$.pipe(
        ofType(maintenanceActions.LOAD_RECURRENCES),
        switchMap((loadAction: LoadRecurrencesAction) => {
            return this.recurrenceService.getRecurrences(loadAction.payload).pipe(
                map((recurrences: any) => {
                    return new maintenanceActions.LoadRecurrencesSuccessAction(
                        recurrences.items.map(recurrence => this.mapRecurrence(recurrence))
                    )
                }),
                catchError((error) => of(new maintenanceActions.LoadRecurrencesFailedAction(error)))
            );
        })
    ));

    updateRecurrence$ = createEffect(() => this.actions$.pipe(
        ofType(maintenanceActions.UPDATE_RECURRENCE),
        switchMap((updateAction: UpdateRecurrenceAction) => {
            return this.recurrenceService.update(updateAction.recurrence).pipe(
                map((updatedRecurrence: any) => {
                    return new maintenanceActions.UpdateRecurrenceSuccessAction(this.mapRecurrence(updatedRecurrence))
                }),
                catchError((error) => of(new maintenanceActions.UpdateRecurrenceFailedAction(error)))
            );
        })
    ));

    deleteRecurrence$ = createEffect(() => this.actions$.pipe(
        ofType(maintenanceActions.DELETE_RECURRENCE),
        switchMap((deleteAction: DeleteRecurrenceAction) => {
            return this.recurrenceService.delete(deleteAction.recurrence.id).pipe(
                map((tools: any) => {
                    return new maintenanceActions.DeleteRecurrenceSuccessAction(this.mapRecurrence(tools))
                }),
                catchError((error) => of(new maintenanceActions.DeleteRecurrenceFailedAction(error)))
            )
        })
    ));

    addActivity$ = createEffect(() => this.actions$.pipe(
        ofType(maintenanceActions.ADD_ACTIVITY),
        switchMap((addAction: any) => {
            return this.activitiesService.createActivity(addAction.payload as Activity).pipe(
                map((activity: any) => {
                    return new maintenanceActions.AddActivitySuccessAction(this.mapActivity(activity))
                }),
                catchError((error) => of(new maintenanceActions.AddActivityFailedAction(error)))
            )
        })
    ));

    loadActivities$ = createEffect(() => this.actions$.pipe(
        ofType(maintenanceActions.LOAD_ACTIVITIES),
        switchMap((loadAction: any) => {
            return this.activitiesService.getActivities(loadAction.payload).pipe(
                map((activities: any) => {
                    return new maintenanceActions.LoadActivitiesSuccessAction(
                        activities.items.map(activity => this.mapActivity(activity))
                            .sort((a, b) => a.priority - b.priority)
                    )
                }),
                catchError((error) => of(new maintenanceActions.LoadActivitiesFailedAction(error)))
            );
        })
    ));

    updateActivities$ = createEffect(() => this.actions$.pipe(
        ofType(maintenanceActions.UPDATE_ACTIVITIES),
        switchMap((updateAction: any) => {
            return this.activitiesService.updateActivities(updateAction.payload).pipe(
                map((activities: any) => {
                    return new maintenanceActions.UpdateActivitiesSuccessAction(
                        activities.items.map(activity => this.mapActivity(activity))
                    )
                }),
                catchError((error) => of(new maintenanceActions.UpdateActivitiesFailedAction(error)))
            );
        })
    ));


    loadActivity$ = createEffect(() => this.actions$.pipe(
        ofType(maintenanceActions.LOAD_ACTIVITY),
        switchMap((loadAction: any) => {
            return this.activitiesService.getActivity(loadAction.payload).pipe(
                map((activity: any) => {
                    return new maintenanceActions.LoadActivitySuccessAction(this.mapActivity(activity))
                }),
                catchError((error) => of(new maintenanceActions.LoadActivityFailedAction(error)))
            );
        })
    ));

    updateActivity$ = createEffect(() => this.actions$.pipe(
        ofType(maintenanceActions.UPDATE_ACTIVITY),
        switchMap((updateAction: UpdateActivityAction) => {
            return this.activitiesService.updateActivity(updateAction.payload).pipe(
                map((updatedActivity: any) => {
                    return new maintenanceActions.UpdateActivitySuccessAction(this.mapActivity(updatedActivity))
                }),
                catchError((error) => of(new maintenanceActions.UpdateActivityFailedAction(error)))
            );
        })
    ));

    deleteActivity$ = createEffect(() => this.actions$.pipe(
        ofType(maintenanceActions.DELETE_ACTIVITY),
        switchMap((deleteAction: DeleteActivityAction) => {
            return this.activitiesService.deleteActivity(deleteAction.payload.id).pipe(
                map((id: number) => {
                    return new maintenanceActions.DeleteActivitySuccessAction(id)
                }),
                catchError((error) => of(new maintenanceActions.DeleteActivityFailedAction(error)))
            );
        })
    ));

    addTask$ = createEffect(() => this.actions$.pipe(
        ofType(maintenanceActions.ADD_TASK),
        mergeMap((addAction: AddTaskAction) => this.tasksService.createTask(addAction.payload, addAction.activity)
            .pipe(
                switchMap((task: any) => {
                    return [
                        new maintenanceActions.AddTaskSuccessAction(this.mapTask(task)),
                        new maintenanceActions.UpdateTaskPhotosAction(task.id, addAction.payload)
                    ];
                }),
                catchError(error => of(new maintenanceActions.AddTaskFailedAction(error)))
            ))
    ));

    loadTasks$ = createEffect(() => this.actions$.pipe(
        ofType(maintenanceActions.LOAD_TASKS),
        switchMap((loadAction: maintenanceActions.LoadTasksAction) => {
            const {searchTerm, activityId} = loadAction.payload;
            return this.tasksService.getTasks(searchTerm, activityId).pipe(
                map((tasks: any) => {
                    return new maintenanceActions.LoadTasksSuccessAction(
                        tasks.items.map(task => this.mapTask(task))
                    )
                }),
                catchError((error) => of(new maintenanceActions.LoadTasksFailedAction(error)))
            );
        })
    ));

    loadTask$ = createEffect(() => this.actions$.pipe(
        ofType(maintenanceActions.LOAD_TASK),
        switchMap((loadAction: any) => {
            return this.tasksService.getTask(loadAction.payload).pipe(
                map((task: any) => {
                    return new maintenanceActions.LoadTaskSuccessAction(this.mapTask(task))
                }),
                catchError((error) => of(new maintenanceActions.LoadTaskFailedAction(error)))
            );
        })
    ));

    updateTaskPhotos$ = createEffect(() => this.actions$.pipe(
        ofType(maintenanceActions.UPDATE_TASK_PHOTOS),
        switchMap((updateTaskPhotosAction: UpdateTaskPhotosAction) => {
            return this.tasksService.updatePhotos(updateTaskPhotosAction.taskId, updateTaskPhotosAction.payload).pipe(
                map((updatedTask: any) => {
                    return new maintenanceActions.UpdateTaskPhotosSuccessAction(this.mapTask(updatedTask))
                }),
                catchError((error) => of(new maintenanceActions.UpdateTaskPhotosFailedAction(error)))
            );
        })
    ));

    updateTask$ = createEffect(() => this.actions$.pipe(
        ofType(maintenanceActions.UPDATE_TASK),
        mergeMap((updateAction: UpdateTaskAction) => this.tasksService.updateTask(updateAction.payload, updateAction.activity)
            .pipe(
                switchMap((task: any) => {
                    return [
                        new maintenanceActions.UpdateTaskSuccessAction(this.mapTask(task)),
                        new maintenanceActions.UpdateTaskPhotosAction(task.id, updateAction.payload)
                    ];
                }),
                catchError(error => of(new maintenanceActions.UpdateTaskFailedAction(error)))
            ))
    ));

    deleteTask$ = createEffect(() => this.actions$.pipe(
        ofType(maintenanceActions.DELETE_TASK),
        switchMap((deleteAction: DeleteTaskAction) => {
            return this.tasksService.deleteTask(deleteAction.payload).pipe(
                map((data: any) => {
                    return new maintenanceActions.DeleteTaskSuccessAction(data)
                }),
                catchError((error) => of(new maintenanceActions.DeleteTaskFailedAction(error)))
            );
        })
    ));

    addPlan$ = createEffect(() => this.actions$.pipe(
        ofType(maintenanceActions.ADD_PLAN),
        switchMap((addAction: any) => {
            return this.plansService.createPlan(addAction.payload).pipe(
                map((plan: any) => {
                    return new maintenanceActions.AddPlanSuccessAction(this.mapPlan(plan))
                }),
                catchError((error) => of(new maintenanceActions.AddPlanFailedAction(error)))
            );
        })
    ));

    loadPlans$ = createEffect(() => this.actions$.pipe(
        ofType(maintenanceActions.LOAD_PLANS),
        switchMap((loadAction: LoadPlansAction) => {
            return this.plansService.getPlans(loadAction.config).pipe(
                map((plans: any) => {
                    return new maintenanceActions.LoadPlansSuccessAction(this.mapPlans(plans), loadAction.config.pager.page === 1)
                }),
                catchError((error) => of(new maintenanceActions.LoadPlansFailedAction(error)))
            );
        })
    ));

    loadPlan$ = createEffect(() => this.actions$.pipe(
        ofType(maintenanceActions.LOAD_PLAN),
        switchMap((loadAction: LoadPlanAction) => {
            return this.plansService.getPlan(loadAction.payload).pipe(
                map((plan: any) => {
                    return new maintenanceActions.LoadPlanSuccessAction(this.mapPlan(plan))
                }),
                catchError((error) => of(new maintenanceActions.LoadPlanFailedAction(error)))
            );
        })
    ));

    deletePlan$ = createEffect(() => this.actions$.pipe(
        ofType(maintenanceActions.DELETE_MAINTENANCE_PLAN),
        switchMap((deleteAction: DeleteMaintenancePlanAction) => {
            return this.plansService.deletePlan(deleteAction.payload.id).pipe(
                map((id: number) => {
                    return new maintenanceActions.DeleteMaintenancePlanSuccessAction(id)
                }),
                catchError((error) => of(new maintenanceActions.DeleteMaintenancePlanFailedAction(error)))
            );
        })
    ));

    loadPlanTools$ = createEffect(() => this.actions$.pipe(
        ofType(maintenanceActions.LOAD_PLAN_TOOLS),
        switchMap((loadAction: LoadPlanToolsAction) => {
            return this.plansService.getPlanTools(loadAction.payload).pipe(
                map((tools: any) => {
                    return new maintenanceActions.LoadPlanToolsSuccessAction(this.groupTools(tools))
                }),
                catchError((error) => of(new maintenanceActions.LoadPlanToolsFailedAction(error)))
            );
        })
    ));

    updatePlan$ = createEffect(() => this.actions$.pipe(
        ofType(maintenanceActions.UPDATE_PLAN),
        switchMap((updateAction: UpdatePlanAction) => {
            return this.plansService.updatePlan(updateAction.payload, updateAction.incrementVersion).pipe(
                map((updatedPlan: any) => {
                    return new maintenanceActions.UpdatePlanSuccessAction(this.mapPlan(updatedPlan))
                }),
                catchError((error) => of(new maintenanceActions.UpdatePlanFailedAction(error)))
            );
        })
    ));

    loadRoles = createEffect(() => this.actions$.pipe(
        ofType(maintenanceActions.LOAD_ROLES),
        switchMap(() => {
            return this.roleService.getAllRoles().pipe(
                map((roles: any) => {
                    return new maintenanceActions.LoadRolesActionSuccessAction(roles)
                }),
                catchError((error) => of(new maintenanceActions.LoadRolesActionFailedAction(error)))
            )
        })
    ));

    loadTaskWorkInstructionFiles = createEffect(() => this.actions$.pipe(
        ofType(maintenanceActions.LOAD_TASK_WORK_INSTRUCTION_FILES),
        switchMap((payload: any) => {
            return this.activitiesService
                .getInstructionLocationsForActivity(payload.operationId, TaskInstructionType.WORK, payload.taskId).pipe(
                    map((files: any) => {
                        return new maintenanceActions.LoadTaskWorkInstructionFilesSuccessAction(files)
                    }),
                    catchError((error) => of(new maintenanceActions.LoadTaskWorkInstructionFilesFailedAction(error)))
                );
        })
    ));

    loadTaskSpareInstructionFiles = createEffect(() => this.actions$.pipe(
        ofType(maintenanceActions.LOAD_TASK_SPARE_INSTRUCTION_FILES),
        switchMap((payload: any) => {
            return this.activitiesService
                .getInstructionLocationsForActivity(payload.operationId, TaskInstructionType.SPARE, payload.taskId).pipe(
                    map((files: any) => {
                        return new maintenanceActions.LoadTaskSpareInstructionFilesSuccessAction(files)
                    }),
                    catchError((error) => of(new maintenanceActions.LoadTaskSpareInstructionFilesFailedAction(error)))
                );
        })
    ));


    constructor(private actions$: Actions,
                private activitiesService: ActivitiesService,
                private recurrenceService: RecurrenceService,
                private roleService: RoleService,
                private tasksService: TasksService,
                private plansService: MaintenancePlansService) {
    }

    private mapRecurrence(recurrence: any) {
        return new Recurrence(recurrence);
    }

    private mapActivity(activity: any) {
        const tasks = activity.operationTasks.map(task => this.mapTask(task));
        return new Activity({...activity, tasks: tasks, nrOfTasks: tasks.length});
    }

    private mapTask(task: any) {
        task.photos = task.photos.map(item => {
            return {id: item.fileId, name: item.fileName, ...item}
        });
        return new ActivityTask(task);
    }

    private mapPlan(plan: any) {
        if (plan.maintenancePlanItems) {
            plan.maintenancePlanItems.forEach(item => {
                item.activity = this.mapActivity(item.operation);
                item.recurrence = this.mapRecurrence(item.recurrence);
                item.activityTask = this.mapTask(item.operationTask);
            });
        }
        return new MaintenancePlan(plan);
    }

    private mapPlans(plans: any) {
        return {
            items: plans.items.map(plan => this.mapPlan(plan)),
            total: plans.total
        }
    }

    private groupTools(tools: any[]) {
        let result = [];
        tools.forEach(group => {
            if (group.tools) {
                result = result.concat(group.tools.map(tool => new Tool(tool)));
            }
            if (group.sets) {
                result = result.concat(group.sets.map(set => new ToolSet(set)));
            }
        });
        return result;
    }
}
